/**
 * Interessentenstamm — Self-Service via magic token
 * ----------------------------------------------------
 * Interessenten können hier:
 *  - ihr Suchprofil aktualisieren (Region, Miete, Zimmer, Features, Haustiere)
 *  - oder die Aufnahme in den Interessentenstamm widerrufen
 *
 * Kein Login — nur mit gültigem Pool-Token (?token=…).
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function InteressentenstammSelfService() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ loading: true, error: null, data: null, prospect: null });
  const [sp, setSp] = useState({});
  const [saving, setSaving] = useState(false);
  const [revoked, setRevoked] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ loading: false, error: "Kein Token in der URL vorhanden.", data: null, prospect: null });
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/public/prospect-pool/profile?token=${encodeURIComponent(token)}`);
        setState({ loading: false, error: null, data, prospect: data.prospect });
        setSp(data.search_profile || {});
      } catch (e) {
        const msg = e?.response?.data?.detail || "Link ist ungültig oder bereits widerrufen.";
        setState({ loading: false, error: msg, data: null, prospect: null });
      }
    })();
  }, [token]);

  const upd = (k, v) => setSp((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/public/prospect-pool/profile?token=${encodeURIComponent(token)}`, {
        purpose: sp.purpose || "rent",
        regions: Array.isArray(sp.regions) ? sp.regions : (typeof sp.regions === "string" ? sp.regions.split(",").map((s) => s.trim()).filter(Boolean) : []),
        object_type: sp.object_type || null,
        min_rooms: toNum(sp.min_rooms),
        max_rooms: toNum(sp.max_rooms),
        min_size_m2: toNum(sp.min_size_m2),
        max_size_m2: toNum(sp.max_size_m2),
        max_rent_cold: toNum(sp.max_rent_cold),
        max_rent_warm: toNum(sp.max_rent_warm),
        desired_move_in: sp.desired_move_in || null,
        features_wanted: Array.isArray(sp.features_wanted) ? sp.features_wanted : (typeof sp.features_wanted === "string" ? sp.features_wanted.split(",").map((s) => s.trim()).filter(Boolean) : []),
        pets: !!sp.pets,
        notes: sp.notes || null,
      });
      toast.success("Suchprofil gespeichert");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
    } finally { setSaving(false); }
  };

  const revoke = async () => {
    if (!confirm("Möchten Sie Ihre Aufnahme in den Interessentenstamm wirklich widerrufen? Sie erhalten dann keine weiteren Benachrichtigungen über neue Immobilien.")) return;
    try {
      await api.post(`/public/prospect-pool/revoke?token=${encodeURIComponent(token)}`);
      setRevoked(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Widerruf");
    }
  };

  if (state.loading) return <Container><p className="text-slate-500">Lade…</p></Container>;
  if (state.error) return (
    <Container>
      <h1 className="text-xl font-semibold text-slate-900">Interessentenstamm</h1>
      <p className="mt-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3">
        {state.error}
      </p>
    </Container>
  );
  if (revoked) return (
    <Container>
      <h1 className="text-xl font-semibold text-slate-900">Widerruf bestätigt</h1>
      <p className="mt-4 text-sm text-slate-700">
        Ihre Aufnahme in den Interessentenstamm wurde widerrufen. Sie erhalten keine weiteren
        Benachrichtigungen über neue Immobilienangebote. Sollten Sie zukünftig wieder Interesse
        haben, können Sie sich jederzeit erneut bewerben.
      </p>
    </Container>
  );

  const regionsText = Array.isArray(sp.regions) ? sp.regions.join(", ") : (sp.regions || "");
  const featuresText = Array.isArray(sp.features_wanted) ? sp.features_wanted.join(", ") : (sp.features_wanted || "");

  return (
    <Container>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">Mein Suchprofil</h1>
        <p className="text-sm text-slate-500 mt-1">
          Hallo {state.prospect?.first_name} {state.prospect?.last_name} —
          halten Sie Ihre Suchkriterien aktuell, damit wir Ihnen passende neue Immobilien
          vorschlagen können.
        </p>
      </div>
      <div className="space-y-4 bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
        <Row label="Miete oder Kauf">
          <select value={sp.purpose || "rent"} onChange={(e) => upd("purpose", e.target.value)}
                  className="h-10 rounded-md border border-slate-200 px-2 text-sm">
            <option value="rent">Miete</option>
            <option value="buy">Kauf</option>
          </select>
        </Row>
        <Row label="Gewünschte Regionen (Städte / Stadtteile)" help="Mehrere mit Komma trennen">
          <Input value={regionsText} onChange={(e) => upd("regions", e.target.value)}
                 placeholder="z. B. Köln, Lohmar, Troisdorf" data-testid="sp-regions" />
        </Row>
        <Row label="Objektart">
          <select value={sp.object_type || ""} onChange={(e) => upd("object_type", e.target.value || null)}
                  className="h-10 rounded-md border border-slate-200 px-2 text-sm">
            <option value="">— egal —</option>
            <option value="apartment">Wohnung</option>
            <option value="house">Haus</option>
            <option value="townhouse">Reihenhaus</option>
            <option value="studio">Appartement</option>
          </select>
        </Row>
        <div className="grid grid-cols-2 gap-3">
          <Row label="Zimmer min">
            <Input type="number" step="0.5" value={sp.min_rooms ?? ""} onChange={(e) => upd("min_rooms", e.target.value)} data-testid="sp-min-rooms" />
          </Row>
          <Row label="Zimmer max">
            <Input type="number" step="0.5" value={sp.max_rooms ?? ""} onChange={(e) => upd("max_rooms", e.target.value)} />
          </Row>
          <Row label="Fläche min (m²)">
            <Input type="number" value={sp.min_size_m2 ?? ""} onChange={(e) => upd("min_size_m2", e.target.value)} />
          </Row>
          <Row label="Fläche max (m²)">
            <Input type="number" value={sp.max_size_m2 ?? ""} onChange={(e) => upd("max_size_m2", e.target.value)} />
          </Row>
          <Row label="Max. Kaltmiete €">
            <Input type="number" value={sp.max_rent_cold ?? ""} onChange={(e) => upd("max_rent_cold", e.target.value)} data-testid="sp-max-cold" />
          </Row>
          <Row label="Max. Gesamtmiete €">
            <Input type="number" value={sp.max_rent_warm ?? ""} onChange={(e) => upd("max_rent_warm", e.target.value)} />
          </Row>
        </div>
        <Row label="Gewünschter Einzug">
          <Input type="date" value={sp.desired_move_in || ""} onChange={(e) => upd("desired_move_in", e.target.value)} />
        </Row>
        <Row label="Gewünschte Ausstattung" help="Mehrere mit Komma trennen (z. B. Balkon, EBK, Aufzug)">
          <Input value={featuresText} onChange={(e) => upd("features_wanted", e.target.value)}
                 placeholder="Balkon, EBK, Aufzug, Garten, Stellplatz" data-testid="sp-features" />
        </Row>
        <Row label="Haustiere">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!sp.pets} onChange={(e) => upd("pets", e.target.checked)} data-testid="sp-pets" />
            Mit Haustier(en)
          </label>
        </Row>
        <Row label="Anmerkung">
          <Textarea rows={2} value={sp.notes || ""} onChange={(e) => upd("notes", e.target.value)}
                    placeholder="Weitere Wünsche, besondere Umstände …" />
        </Row>
        <div className="flex items-center justify-between pt-3 border-t">
          <Button onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="sp-save">
            {saving ? "Speichere…" : "Suchprofil speichern"}
          </Button>
          <Button variant="outline" onClick={revoke} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="sp-revoke">
            Interessentenstamm widerrufen
          </Button>
        </div>
      </div>
      <p className="text-[11px] text-slate-500 mt-6">
        Hinweis zum Datenschutz: Ihre Angaben werden ausschließlich für die Übermittlung passender
        Immobilienangebote verwendet. Ihre Einwilligung können Sie jederzeit mit Wirkung für die
        Zukunft widerrufen.
      </p>
    </Container>
  );
}

function Container({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">{children}</div>
    </div>
  );
}

function Row({ label, help, children }) {
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      {children}
      {help && <div className="text-[10px] text-slate-400 mt-0.5">{help}</div>}
    </div>
  );
}

function toNum(v) {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
